// Answer / dismiss controls for one of Merlin's questions (kanban 816b31c3).
//
// Admin ask, 2026-09-21: "I need to be able to answer Merlin's questions in
// text or just dismiss them." This sits in the foot of each question card on
// the Merlin's-questions tab. A response is stored server-side as PENDING and
// handed to the next roadmap run, which applies the answer and drops the
// question — so the card shows "answered, waiting for Merlin" (with Edit /
// Undo) rather than disappearing the moment the admin hits Send. The meeting
// flow agreed on 2026-09-08 still works unchanged: answers can also come from
// the next meeting's transcript.
//
// Owner ask, 2026-09-28 (kanban 09e61810): "people need to be able to just
// record into it, just talk about them" — with fifty-odd questions a run,
// typing every answer is the bottleneck. "Record answer" opens the box and
// starts the microphone in one click; the Whisper transcript lands in the box
// for a read-through, and "Send to Merlin" is the same path as a typed answer.
// Talk again to add more; type to fix a word. Nothing is sent until Send.

import { useCallback, useRef, useState } from 'react';
import type {
  QuestionIdentity,
  QuestionResponse,
  QuestionResponseRequest,
} from '../../services/roadmapReports';
import {
  appendTranscript,
  formatDictationDuration,
  useVoiceDictation,
} from '../../hooks/useVoiceDictation';
import styles from './RoadmapV2.module.css';

export type RespondFn = (req: QuestionResponseRequest) => Promise<void>;
export type WithdrawFn = (id: QuestionIdentity) => Promise<void>;

interface Props {
  identity: QuestionIdentity;
  response: QuestionResponse | null | undefined;
  onRespond: RespondFn;
  onWithdraw: WithdrawFn;
  /** Placeholder for the answer box — the unplaced list asks a different thing. */
  placeholder?: string;
  /** Label for the answer action ("Answer" on a question, "Tell Merlin" on an unplaced item). */
  answerLabel?: string;
}

function errorMessage(err: unknown): string {
  const detail = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (typeof detail?.data?.detail === 'string') return detail.data.detail;
  if (detail?.status === 403) return 'Only an admin can respond to Merlin.';
  return 'Could not save that — try again.';
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12" aria-hidden="true">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z" />
      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

export function QuestionResponseControls({
  identity,
  response,
  onRespond,
  onWithdraw,
  placeholder = 'Type the answer for Merlin — a date, a name, a decision…',
  answerLabel = 'Answer',
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The transcript lands whenever Whisper answers; append to what is in the
  // box THEN, not to the draft captured when recording started.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const onTranscript = useCallback((transcript: string) => {
    const next = appendTranscript(draftRef.current, transcript);
    draftRef.current = next;
    setDraft(next);
  }, []);
  const dictation = useVoiceDictation({ onTranscript });
  const micBusy = dictation.isRecording || dictation.transcribing || dictation.requestingPermission;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setEditing(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const send = () => {
    const text = draft.trim();
    if (!text) {
      setError('Write or record the answer first, or dismiss the question instead.');
      return;
    }
    void run(() => onRespond({ ...identity, kind: 'answered', answer: text }));
  };
  const dismiss = () => void run(() => onRespond({ ...identity, kind: 'dismissed' }));
  const undo = () => void run(() => onWithdraw(identity));
  const openEditor = (initial: string) => {
    draftRef.current = initial;
    setDraft(initial);
    setError(null);
    setEditing(true);
  };
  const startEditing = () => openEditor(response?.answer ?? '');
  const startRecording = () => {
    openEditor(response?.answer ?? '');
    dictation.start();
  };
  const cancelEditing = () => {
    dictation.cancel();
    setEditing(false);
  };

  const when = response ? response.responded_at.slice(0, 10) : '';
  const sendBlocked = busy || micBusy;
  const sendTitle = dictation.isRecording
    ? 'Stop the recording first'
    : dictation.transcribing
      ? 'Waiting for the transcript'
      : undefined;

  return (
    <div className={styles.respond} data-testid="question-response">
      {response && !editing && (
        <div
          className={`${styles.respondState} ${response.kind === 'answered' ? styles.respondAnswered : styles.respondDismissed}`}
          role="status"
        >
          {response.kind === 'answered' ? (
            <>
              <span className={styles.respondLabel}>
                Answered by {response.responded_by_email} on {when} · waiting for Merlin&apos;s next run
              </span>
              <blockquote className={styles.respondAnswer}>{response.answer}</blockquote>
            </>
          ) : (
            <span className={styles.respondLabel}>
              Dismissed by {response.responded_by_email} on {when} · Merlin won&apos;t ask this again
            </span>
          )}
        </div>
      )}

      {editing && (
        <div className={styles.respondEditor}>
          <textarea
            className={styles.respondInput}
            aria-label="Your answer"
            placeholder={dictation.isSupported ? `${placeholder} Or press Record and just talk.` : placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            disabled={busy}
            autoFocus
          />
          {dictation.isSupported && (
            <div className={styles.respondDictation}>
              <button
                type="button"
                className={`${styles.respondMic} ${dictation.isRecording ? styles.respondMicRecording : ''}`}
                onClick={dictation.toggle}
                disabled={busy || dictation.transcribing || dictation.requestingPermission}
                aria-pressed={dictation.isRecording}
              >
                {dictation.isRecording ? <StopIcon /> : <MicIcon />}
                {dictation.isRecording ? 'Stop recording' : draft.trim() ? 'Record more' : 'Record'}
              </button>
              {dictation.isRecording && (
                <span className={styles.respondRecording} role="status">
                  <span className={styles.respondRecordingDot} aria-hidden="true" />
                  Recording {formatDictationDuration(dictation.duration)} · talk, then press Stop
                </span>
              )}
              {dictation.requestingPermission && (
                <span className={styles.respondHint} role="status">Waiting for microphone permission…</span>
              )}
              {dictation.transcribing && (
                <span className={styles.respondTranscribing} role="status">
                  <span className={styles.respondSpinner} aria-hidden="true" />
                  Turning your recording into text…
                </span>
              )}
              {dictation.error && <span className={styles.respondError} role="alert">{dictation.error}</span>}
            </div>
          )}
        </div>
      )}

      <div className={styles.respondActions}>
        {editing ? (
          <>
            <button
              type="button"
              className={styles.respondPrimary}
              onClick={send}
              disabled={sendBlocked}
              title={sendTitle}
            >
              {busy ? 'Sending…' : 'Send to Merlin'}
            </button>
            <button type="button" className={styles.respondGhost} onClick={cancelEditing} disabled={busy}>
              Cancel
            </button>
          </>
        ) : response ? (
          <>
            {response.kind === 'answered' && (
              <button type="button" className={styles.respondGhost} onClick={startEditing} disabled={busy}>
                Edit answer
              </button>
            )}
            <button type="button" className={styles.respondGhost} onClick={undo} disabled={busy}>
              {busy ? 'Undoing…' : 'Undo'}
            </button>
          </>
        ) : (
          <>
            <button type="button" className={styles.respondPrimary} onClick={startEditing} disabled={busy}>
              {answerLabel}
            </button>
            {dictation.isSupported && (
              <button
                type="button"
                className={`${styles.respondGhost} ${styles.respondRecordAction}`}
                onClick={startRecording}
                disabled={busy}
                title="Open the answer box and start the microphone — just talk"
              >
                <MicIcon />
                Record answer
              </button>
            )}
            <button
              type="button"
              className={styles.respondGhost}
              onClick={dismiss}
              disabled={busy}
              title="Merlin drops this question and will not ask it again in these words"
            >
              {busy ? 'Dismissing…' : 'Dismiss'}
            </button>
          </>
        )}
        {error && <span className={styles.respondError} role="alert">{error}</span>}
      </div>
    </div>
  );
}
